import { NavLink, AppShell } from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';

import { LegalLinks } from '../Legal';

import {
  getEnabledNavRoutes,
  NAV_GROUPS,
  NAV_SLOTS,
  NavRoute,
  NavSectionName,
} from '@/config/navigation';
import { useAccount, usePermissions } from '@/features/auth';
import { isReviewAccount } from '@/utils';

type Props = {
  opened: boolean;
};

type NavGroupProps = {
  name: string;
  pathname: string;
  canView: (route: NavRoute) => boolean;
};

type NavSectionProps = {
  section: NavSectionName;
  pathname: string;
  canView: (route: NavRoute) => boolean;
};

function isActive(path: string, currentPath: string): boolean {
  return currentPath === path || currentPath.startsWith(path + '/');
}

function NavGroup({ name, pathname, canView }: NavGroupProps) {
  const group = NAV_GROUPS[name];
  const items = getEnabledNavRoutes().filter(
    (route) => route.navLabel !== undefined && route.group === name && canView(route)
  );

  if (items.length === 0) {
    return null;
  }

  const parents = Array.from(
    new Set(
      items.map((route) => route.parent).filter((parent): parent is string => parent !== undefined)
    )
  );
  const directItems = items.filter((route) => route.parent === undefined);
  const GroupIcon = group.icon;

  return (
    <NavLink label={name} leftSection={<GroupIcon size={25} stroke={1.5} />}>
      {parents.map((parent) => {
        const parentItems = items.filter((route) => route.parent === parent);
        return (
          <NavLink key={parent} label={parent}>
            {parentItems.map((route) => {
              const Icon = route.icon;
              return (
                <NavLink
                  key={route.path}
                  active={isActive(route.path, pathname)}
                  label={route.navLabel}
                  component={Link}
                  to={route.path}
                  leftSection={Icon ? <Icon size={25} stroke={1.5} /> : undefined}
                />
              );
            })}
          </NavLink>
        );
      })}
      {directItems.map((route) => {
        const Icon = route.icon;
        return (
          <NavLink
            key={route.path}
            active={isActive(route.path, pathname)}
            label={route.navLabel}
            component={Link}
            to={route.path}
            leftSection={Icon ? <Icon size={25} stroke={1.5} /> : undefined}
          />
        );
      })}
    </NavLink>
  );
}

function NavSection({ section, pathname, canView }: NavSectionProps) {
  const items = getEnabledNavRoutes().filter(
    (route) => route.navLabel !== undefined && route.section === section && canView(route)
  );

  return (
    <>
      {items.map((route) => {
        const Icon = route.icon;
        return (
          <NavLink
            key={route.path}
            active={isActive(route.path, pathname)}
            label={route.navLabel}
            component={Link}
            to={route.path}
            leftSection={Icon ? <Icon size={25} stroke={1.5} /> : undefined}
          />
        );
      })}
    </>
  );
}

export const NavBar = ({ opened }: Props) => {
  const { pathname } = useLocation();
  const { can } = usePermissions();
  const getAccount = useAccount();

  const canView = (route: NavRoute) => {
    if (route.group === 'Bot' && isReviewAccount(getAccount.data?.account._id || '')) {
      return false;
    }
    if (route.permission === undefined) {
      return true;
    }
    return can(route.permission);
  };

  return (
    <AppShell.Navbar p="md" hidden={!opened}>
      <AppShell.Section grow style={{ overflowY: 'auto', marginRight: -15 }}>
        {NAV_SLOTS.map((slot) =>
          slot.type === 'group' ? (
            <NavGroup key={slot.name} name={slot.name} pathname={pathname} canView={canView} />
          ) : (
            <NavSection key={slot.name} section={slot.name} pathname={pathname} canView={canView} />
          )
        )}
      </AppShell.Section>
      <AppShell.Section>
        <LegalLinks />
      </AppShell.Section>
    </AppShell.Navbar>
  );
};
